import PropTypes from 'prop-types';

import { trans } from '@spotahome/soyuz/client';

import { useMemo } from 'react';

import Button from '../../../Button';
import DSIcon from '../../../../../base/DSIcon';

import FeatureService from '../../../../services/FeatureService';

import {
  APARTMENT,
  STUDIO,
  ROOM_SHARED,
  LISTING_TYPES,
  isRoom
} from '../../../../data/common/property';

import CloseButton from '../../../CloseButton';

import { useFavorite } from '../../../../context/FavoriteContext';

import styles from './HomecardDescriptionRedesign.module.scss';

import {
  STUDIO_AND_APARTMENT_FEATURES,
  ROOM_SHARED_AND_RESIDENCE_FEATURES
} from './features';

const FEATURES_LIMIT = 7;
const FEATURES_MAX_WITHOUT_MESSAGE = 8;

const getFeaturesToShow = (type, listingFeatures) => {
  const features = [APARTMENT, STUDIO].includes(type)
    ? STUDIO_AND_APARTMENT_FEATURES
    : ROOM_SHARED_AND_RESIDENCE_FEATURES;

  const featuresToShow = {};

  features.forEach((featureId, i) => {
    if (
      FeatureService.isFeatureAvailableToShow(
        featureId,
        listingFeatures?.[featureId] ?? false
      )
    ) {
      featuresToShow[featureId] = {
        value: listingFeatures?.[featureId],
        order: i
      };
    }
  });

  return featuresToShow;
};

export const formatFeatures = featuresToShow => {
  const formattedFeatures = FeatureService.getFeaturesAsList(featuresToShow);
  const availableFeatures =
    FeatureService.getAvailableFeaturesList(formattedFeatures);

  const featuresLimit =
    availableFeatures.length <= FEATURES_MAX_WITHOUT_MESSAGE
      ? FEATURES_MAX_WITHOUT_MESSAGE
      : FEATURES_LIMIT;

  return availableFeatures
    .slice(0, featuresLimit)
    .sort((a, b) => a.order - b.order)
    .map(feature => ({
      renderIcon: () => (
        <DSIcon
          className={styles['homecard-description-redesign__feature-icon']}
          key={feature.id}
          name={FeatureService.getIconFromFeatureId(feature.id)}
          title={FeatureService.getNameFromFeatureId(feature.id, feature.value)}
        />
      ),
      label: FeatureService.getNameFromFeatureId(feature.id, feature.value)
    }));
};

const getAmenities = (area, numberOfBedrooms, numberOfBathrooms, type) => {
  const amenities = [
    {
      id: 'single_bed',
      renderIcon: () => <DSIcon name="single_bed" />,
      content: numberOfBedrooms,
      show: numberOfBedrooms > 0
    },
    {
      id: 'shower',
      renderIcon: () => <DSIcon name="shower" />,
      content: numberOfBathrooms,
      show: numberOfBathrooms > 0
    },
    {
      id: 'area_m2',
      renderIcon: () => {},
      content: `${area} m²`,
      show: !isRoom(type) && area > 0
    }
  ];

  return amenities.filter(amenity => amenity.show);
};

const HomecardDescriptionRedesign = ({
  type = ROOM_SHARED,
  link = '',
  linkTarget = '',
  description = null,
  onCloseClick,
  pageType = ''
}) => {
  const { area, numberOfBedrooms, numberOfBathrooms, content, features } =
    description;

  const { isFavorite, toggleFavorite } = useFavorite({
    section: 'homecard_description',
    pageType
  });

  const amenities = useMemo(
    () => getAmenities(area, numberOfBedrooms, numberOfBathrooms, type),
    [area, numberOfBedrooms, numberOfBathrooms, type]
  );

  const featuresToShow = useMemo(
    () => getFeaturesToShow(type, features),
    [type, features]
  );

  const featuresCount = Object.keys(featuresToShow).length;

  const formattedFeatures = useMemo(
    () => formatFeatures(featuresToShow),
    [featuresToShow]
  );

  const favoriteIconName = isFavorite ? 'fav_filled' : 'fav_line';

  return (
    <div className={styles['homecard-description-redesign']}>
      <CloseButton
        className={styles['homecard-description-redesign__close-button']}
        size="small"
        onClick={onCloseClick}
      />
      {amenities.length > 0 && (
        <div className={styles['homecard-description-redesign__amenities']}>
          {amenities.map(amenity => (
            <div
              key={amenity.id}
              className={styles['homecard-description-redesign__amenity']}
            >
              {amenity.renderIcon()}
              {amenity.content}
            </div>
          ))}
        </div>
      )}
      <div className={styles['homecard-description-redesign__content']}>
        {content}
      </div>

      <div className={styles['homecard-description-redesign__features']}>
        {formattedFeatures.map(feature => (
          <div
            key={feature.id}
            className={styles['homecard-description-redesign__feature']}
          >
            {feature.renderIcon()}
            {feature.label}
          </div>
        ))}

        {featuresCount > FEATURES_MAX_WITHOUT_MESSAGE && (
          <div
            className={styles['homecard-description-redesign__features-more']}
          >
            {trans('homecard.description.features.more', {
              count: featuresCount - FEATURES_LIMIT
            })}
          </div>
        )}
      </div>

      <div className={styles['homecard-description-redesign__footer']}>
        <Button
          color="tertiary"
          size="medium"
          onClick={toggleFavorite}
          renderIcon={() => <DSIcon name={favoriteIconName} />}
          className={styles['homecard-description-redesign__footer-button']}
        >
          {isFavorite
            ? trans('listing.social-buttons.unfavorite')
            : trans('listing.social-buttons.favorite')}
        </Button>
        <Button
          href={link}
          target={linkTarget}
          color="secondary"
          size="medium"
          className={styles['homecard-description-redesign__footer-button']}
        >
          {trans('homecard.description.view-more')}
        </Button>
      </div>
    </div>
  );
};

HomecardDescriptionRedesign.propTypes = {
  type: PropTypes.oneOf(LISTING_TYPES),
  link: PropTypes.string,
  linkTarget: PropTypes.string,
  description: PropTypes.shape({
    area: PropTypes.number,
    numberOfBathrooms: PropTypes.number,
    features: PropTypes.shape({
      [PropTypes.string]: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool
      ])
    }).isRequired,
    content: PropTypes.string
  }),
  onCloseClick: PropTypes.func.isRequired,
  pageType: PropTypes.string
};

export default HomecardDescriptionRedesign;
